/**
 * StockBulkInPage — Sprint S7 · 2026-02c
 *
 * Minimal admin form to log a supplier delivery in one POST:
 *   pick supplier → optional invoice/PO ref → add rows (item + qty)
 *   → submit batch to /api/inventory/bulk-in
 *
 * Structural per Trevor 2026-02c ("we only need the structure for
 * now"). Deliberately spartan · no supplier autocomplete · no fancy
 * search · one screen · one form.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Truck, Loader2, CheckCircle2 } from 'lucide-react';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` },
});

export default function StockBulkInPage() {
  const [supplier, setSupplier] = useState('');
  const [refId, setRefId] = useState('');
  const [rows, setRows] = useState([{ inventory_item_id: '', delta: '', notes: '' }]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [err, setErr] = useState(null);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get('/inventory');
      setItems((data || []).filter(i => i.active !== false));
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, []);
  useEffect(() => { loadItems(); }, [loadItems]);

  const addRow = () => setRows(r => [...r, { inventory_item_id: '', delta: '', notes: '' }]);
  const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const patchRow = (i, patch) => setRows(r => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));

  const validRows = useMemo(() => rows.filter(r => r.inventory_item_id && Number(r.delta) > 0), [rows]);
  const canSubmit = validRows.length > 0 && !saving;

  const submit = async () => {
    setSaving(true);
    setErr(null);
    setFlash(null);
    try {
      const { data } = await api.post('/inventory/bulk-in', {
        supplier: supplier.trim(),
        ref_id:   refId.trim(),
        rows:     validRows.map(r => ({
          inventory_item_id: r.inventory_item_id,
          delta:             Number(r.delta),
          notes:             r.notes || '',
        })),
      });
      setFlash(`Logged ${data.count} movement${data.count === 1 ? '' : 's'}`);
      setRows([{ inventory_item_id: '', delta: '', notes: '' }]);
      await loadItems();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="stock-bulk-in-page">
      <div className="max-w-4xl mx-auto p-6">
        <Link to="/" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800 mb-3">
          <ArrowLeft size={12} /> Back
        </Link>

        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-5">
          <div>
            <h1 className="text-lg font-extrabold tracking-tight text-slate-900 inline-flex items-center gap-2">
              <Truck size={16} className="text-orange-600" /> Stock Bulk-In
            </h1>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Log a supplier delivery in one batch — every row credits stock and writes a canonical <code className="text-[10px] bg-slate-100 px-1 rounded">bulk_in</code> movement tied to this receipt.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Supplier</span>
              <input
                data-testid="bulk-in-supplier"
                value={supplier}
                onChange={e => setSupplier(e.target.value)}
                placeholder="Bunnings / Steel Roofing / …"
                className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">PO / Invoice ref (optional)</span>
              <input
                data-testid="bulk-in-ref"
                value={refId}
                onChange={e => setRefId(e.target.value)}
                placeholder="INV-42 · PO-192"
                className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
              />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rows</div>
              <button
                data-testid="bulk-in-add-row"
                onClick={addRow}
                className="inline-flex items-center gap-1 text-xs text-orange-700 hover:text-orange-900 font-semibold"
              >
                <Plus size={12} /> Add row
              </button>
            </div>
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_100px_1fr_auto] gap-2 items-center" data-testid={`bulk-in-row-${i}`}>
                  <select
                    value={r.inventory_item_id}
                    onChange={e => patchRow(i, { inventory_item_id: e.target.value })}
                    className="px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
                  >
                    <option value="">Pick item…</option>
                    {items.map(it => (
                      <option key={it._id} value={it._id}>
                        {it.sku ? `[${it.sku}] ` : ''}{it.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Qty"
                    value={r.delta}
                    onChange={e => patchRow(i, { delta: e.target.value })}
                    className="px-2 py-1.5 border border-slate-300 rounded text-sm font-mono-num text-right"
                  />
                  <input
                    placeholder="Notes (colour, batch, etc.)"
                    value={r.notes}
                    onChange={e => patchRow(i, { notes: e.target.value })}
                    className="px-2 py-1.5 border border-slate-300 rounded text-sm"
                  />
                  <button
                    onClick={() => removeRow(i)}
                    className="p-1.5 text-slate-400 hover:text-rose-600"
                    title="Remove row"
                    disabled={rows.length === 1}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {err && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded p-2 text-sm" data-testid="bulk-in-error">{err}</div>
          )}
          {flash && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-2 text-sm inline-flex items-center gap-1" data-testid="bulk-in-flash">
              <CheckCircle2 size={14} /> {flash}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1 border-t border-slate-100">
            <span className="text-[11px] text-slate-500">
              {validRows.length} valid row{validRows.length === 1 ? '' : 's'}
            </span>
            <button
              data-testid="bulk-in-submit"
              onClick={submit}
              disabled={!canSubmit}
              className="px-3 py-1.5 bg-orange-600 text-white rounded text-sm font-semibold hover:bg-orange-700 disabled:bg-slate-300 disabled:cursor-not-allowed inline-flex items-center gap-1"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Truck size={13} />}
              Log delivery
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
